import React, { FC, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { When } from 'react-if'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { ContinueWatchingCollection } from '@/organisms/WatchNowCollection/ContinueWatchingCollection'
import {
  ContinueWatchingProject,
  GET_CONTINUE_WATCHING_DATA,
} from '@/organisms/WatchNowCollection/ContinueWatchingCollection/data'
import { StartWatchingCollection } from '@/organisms/WatchNowCollection/StartWatchingCollection'
import { useGuildMember } from '@/services/GuildUserService'
import { LinkViewModel } from '@/services/RenderService'
import { useUser } from '@/services/UserService'
import { GetContinueWatchingQuery } from '@/types/codegen-federation'
import { isDefined } from '@/utils/types'
import { PlaceholderLandscapeCollection } from '../PlaceholderLandscapeCollection'
import { TopPicksForYouCollection } from './TopPicksForYouCollection'

export interface WatchNowCollectionProps {
  contentLinks: LinkViewModel[]
  title: string
  onlyShowContinue?: boolean
  onlyShowFree?: boolean
}

export const WatchNowCollection: FC<WatchNowCollectionProps> = ({
  contentLinks,
  title,
  onlyShowContinue = false,
  onlyShowFree = false,
}) => {
  const { isLoggedIn = false, loading: isLoadingUser = false } = useUser()

  const { data, loading: isLoadingContinueWatching } = useQuery<GetContinueWatchingQuery>(GET_CONTINUE_WATCHING_DATA, {
    skip: !isLoggedIn,
    errorPolicy: 'all',
  })

  const projectsToContinue = useMemo(
    () =>
      data?.projects
        ?.filter((project) => project?.continueWatching?.watchPosition)
        ?.filter(isDefined)
        ?.filter((project) => project?.slug === project?.continueWatching?.projectSlug)
        .sort(sortByWatchPosition) ?? [],
    [data?.projects],
  )

  const { isGuildMember } = useGuildMember()

  if (isLoadingUser || isLoadingContinueWatching) {
    return <PlaceholderLandscapeCollection />
  }

  const freeContentLinks = contentLinks.filter((contentLink) => {
    const episode = contentLink?.metadata?.episode as
      | { earlyAccessDate?: string; publiclyAvailableDate?: string }
      | undefined

    if (!episode?.publiclyAvailableDate) {
      return false
    }

    const publiclyAvailableDate = new Date(episode.publiclyAvailableDate)
    const now = new Date()

    return publiclyAvailableDate < now
  })

  if (isLoggedIn && projectsToContinue.length > 0) {
    return (
      <>
        <PaddedContainer className="overflow-x-hidden" fluid>
          <div className="flex flex-col gap-y-12">
            <When condition={onlyShowContinue}>
              <ContinueWatchingCollection toContinueWatching={projectsToContinue} />
            </When>
            <When condition={!onlyShowContinue}>
              <ContinueWatchingCollection toContinueWatching={projectsToContinue} />
              {isGuildMember ? (
                <StartWatchingCollection
                  contentLinks={onlyShowFree ? freeContentLinks : contentLinks}
                  toContinueWatching={projectsToContinue}
                  title={title}
                />
              ) : (
                <TopPicksForYouCollection />
              )}
            </When>
          </div>
        </PaddedContainer>
      </>
    )
  } else {
    return (
      <>
        <When condition={!onlyShowContinue}>
          <PaddedContainer className="overflow-x-hidden" fluid>
            <StartWatchingCollection contentLinks={onlyShowFree ? freeContentLinks : contentLinks} title={title} />
          </PaddedContainer>
        </When>
      </>
    )
  }
}

function sortByWatchPosition(a: ContinueWatchingProject, b: ContinueWatchingProject) {
  return new Date(a?.continueWatching?.watchPosition?.updatedAt as string) >=
    new Date(b?.continueWatching?.watchPosition?.updatedAt as string)
    ? -1
    : 1
}
